import React, { useEffect, useState } from "react";
import axios from "axios";
import Select from "react-select";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleExclamation, faPen } from "@fortawesome/free-solid-svg-icons";
import { selectProduct, setCooksCategory } from "../../redux/slices/itemSlice";
import { selectUser } from "../../redux/slices/userSlice";
import { getUserRoles, hasRole } from "../../utils/roles";
import Loading from "../Loading";

const API = "https://api.sedihisham.com";

const cookName = (cook, locale) =>
  cook.cooks_translations?.find((t) => t.locale === locale)?.name;

const cookLabel = (cook) =>
  [cookName(cook, "en"), cookName(cook, "ar")].filter(Boolean).join(" — ") ||
  `#${cook.id}`;

// Cooks (recipes) linked to the selected product. Editable when the server
// has the cook_products endpoints (GET/POST /cooks/byproduct/:id); otherwise
// read-only, and links are edited from each cook's "Linked Products".
function LinkedCooks() {
  const dispatch = useDispatch();
  const selectedProduct = useSelector(selectProduct);
  const currentUser = useSelector(selectUser);
  const disabled = !hasRole(getUserRoles(currentUser), ["DataEntry", "Admin"]);
  const productId = selectedProduct?.id;

  const [cooks, setCooks] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [editable, setEditable] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    const [allRes, linkedRes] = await Promise.allSettled([
      axios.get(`${API}/cooks/findall`),
      axios.get(`${API}/cooks/byproduct/${productId}`),
    ]);
    const all =
      allRes.status === "fulfilled" && Array.isArray(allRes.value.data)
        ? allRes.value.data
        : [];
    setCooks(all);
    if (linkedRes.status === "fulfilled" && Array.isArray(linkedRes.value.data)) {
      setEditable(true);
      setSelectedIds(linkedRes.value.data.map((cook) => cook.id));
    } else {
      setEditable(false);
      setSelectedIds(
        all
          .filter((cook) =>
            (cook.recommended_products || []).some(
              (product) => product.id === productId
            )
          )
          .map((cook) => cook.id)
      );
    }
  };

  useEffect(() => {
    if (productId) load();
  }, [productId]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await axios.post(`${API}/cooks/byproduct/${productId}`, {
        cook_ids: selectedIds,
      });
      if (Array.isArray(response.data)) {
        setSelectedIds(response.data.map((cook) => cook.id));
      }
      Swal.fire({
        icon: "success",
        title: "Your changes had been saved",
        showConfirmButton: false,
        timer: 1500,
      });
    } catch (err) {
      console.log(err);
      const message = err.response?.data?.message;
      setError(
        Array.isArray(message)
          ? message.join(", ")
          : message || "Could not save the linked cooks, please try again."
      );
    }
    setSaving(false);
  };

  // cooks whose category was deleted no longer appear anywhere in the dashboard
  const options = (cooks || [])
    .filter((cook) => cook.cooks_categoryId)
    .map((cook) => ({
    value: cook.id,
    label: cookLabel(cook),
  }));
  const selectedOptions = selectedIds
    .map((id) => options.find((option) => option.value === id))
    .filter(Boolean);
  const linkedCooks = selectedIds
    .map((id) => (cooks || []).find((cook) => cook.id === id))
    .filter(Boolean);

  return (
    <div className="w-[91%] pb-10">
      <p className="text-[14px] font-[700] text-[#000]">
        Cooks linked to this product
      </p>
      {editable ? (
        <p className="text-[13px] font-[400] text-[#707070] mt-1">
          These cooks are shown on the product page of the website, and the
          product is shown on each cook page. Add or remove cooks, then click
          Save. A product can be linked to several cooks.
        </p>
      ) : (
        <p className="text-[13px] font-[400] text-[#707070] mt-1">
          These cooks are shown on the product page of the website. To link
          this product to a cook, open the cook (Kitchen → category → cook) and
          select the product in "Linked Products".
        </p>
      )}

      {cooks === null ? (
        <p className="mt-4 text-[14px] font-[400]">Loading...</p>
      ) : (
        <>
          {editable && (
            <div className="mt-4 flex items-start gap-3">
              <div className="grow text-[14px] font-[400]">
                <Select
                  isMulti
                  isDisabled={disabled}
                  options={options}
                  value={selectedOptions}
                  onChange={(chosen) =>
                    setSelectedIds((chosen || []).map((option) => option.value))
                  }
                  placeholder="Search cooks by English or Arabic name..."
                  noOptionsMessage={() => "No cooks found"}
                />
              </div>
              <div
                onClick={() => !disabled && !saving && save()}
                className="cursor-pointer rounded-full text-[13px] font-[400] px-6 py-[6px] text-[#fff] bg-[#0388CC]"
              >
                Save
              </div>
            </div>
          )}
          {error ? (
            <p className="text-[14px] font-[400] mt-2 text-[red]">
              <FontAwesomeIcon icon={faCircleExclamation} /> {error}
            </p>
          ) : null}
          <div className="mt-4 divide-y">
            {linkedCooks.length === 0 ? (
              <p className="text-[14px] font-[400]">
                This product is not linked to any cook yet.
              </p>
            ) : (
              linkedCooks.map((cook) => (
                <div key={cook.id} className="flex items-center py-2">
                  <img
                    className="h-[45px] w-[60px] object-cover rounded mr-4"
                    src={`${API}/${cook.image_background_url}`}
                    onError={({ currentTarget }) => {
                      currentTarget.onerror = null;
                      currentTarget.src = "/images/no-image.jpeg";
                    }}
                    alt=""
                  />
                  <div className="grow text-[14px] font-[400]">
                    <div className="text-[#0388CC]">{cookName(cook, "en")}</div>
                    <div dir="rtl" className="text-left">
                      {cookName(cook, "ar")}
                    </div>
                  </div>
                  <Link
                    to={`/cook-edit?id=${cook.id}`}
                    onClick={() =>
                      dispatch(setCooksCategory({ id: cook.cooks_categoryId }))
                    }
                    className="text-[13px] font-[400] rounded-full px-4 py-1 text-[#fff] bg-[#0388CC]"
                  >
                    <FontAwesomeIcon icon={faPen} className="mr-2" />
                    Edit cook
                  </Link>
                </div>
              ))
            )}
          </div>
        </>
      )}
      {saving ? <Loading /> : null}
    </div>
  );
}

export default LinkedCooks;
